import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas } from '../lib/rutas';
import {
  Target,
  TrendingUp,
  Settings,
  X,
  ChevronLeft,
  ChevronRight,
  Info,
  ShoppingCart,
  Calendar,
} from 'lucide-react';
import { isWorkday } from '../lib/workdays';

interface RutaGoal {
  id: string;
  ruta: number;
  collection_target: number;
  profit_pct: number;
  expenses_pct: number;
  loss_pct: number;
  reinvestment_pct: number;
  sales_target: number | null;
}

interface RutaMetrics {
  ruta: number;
  committedNextMonth: number;
  receivedNextMonth: number;
  salesDoneThisMonth: number;
  salesCount: number;
  goal: RutaGoal | null;
}

const DEFAULT_COLLECTION_TARGET = 70000;
const DEFAULT_REINVESTMENT_PCT = 57.14;

export default function Metas() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<RutaMetrics[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configGoals, setConfigGoals] = useState<{ ruta: number; target: number }[]>([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [avgTicket, setAvgTicket] = useState(0);

  const [salesYear, salesMonth] = selectedMonth.split('-').map(Number);
  const salesStart = new Date(salesYear, salesMonth - 1, 1);
  const salesEnd = new Date(salesYear, salesMonth, 0, 23, 59, 59, 999);

  const targetDate = new Date(salesYear, salesMonth, 1);
  const targetYear = targetDate.getFullYear();
  const targetMonth = targetDate.getMonth() + 1;
  const targetStart = new Date(targetYear, targetMonth - 1, 1);
  const targetEnd = new Date(targetYear, targetMonth, 0, 23, 59, 59, 999);

  const today = new Date();
  const isCurrentSalesMonth =
    today.getFullYear() === salesYear && today.getMonth() + 1 === salesMonth;

  useEffect(() => {
    if (user?.role === 'manager') fetchData();
  }, [user, selectedMonth]);

  const fetchData = async () => {
    if (!user?.unit_id) return;
    setLoading(true);
    try {
      const [goalsResult, committedResult, receivedResult, salesResult, avgTicketResult] =
        await Promise.all([
          fetchGoals(),
          fetchCommittedReceivables(),
          fetchReceivedNextMonth(),
          fetchSalesThisMonth(),
          fetchAvgTicket(),
        ]);

      setAvgTicket(avgTicketResult);

      const rutas = getVisibleRutas(user);
      const combined: RutaMetrics[] = rutas.map(ruta => ({
        ruta,
        committedNextMonth: committedResult[ruta] || 0,
        receivedNextMonth: receivedResult[ruta] || 0,
        salesDoneThisMonth: salesResult.amounts[ruta] || 0,
        salesCount: salesResult.counts[ruta] || 0,
        goal: goalsResult.find(g => g.ruta === ruta) || null,
      }));

      setMetrics(
        combined.filter(
          m => m.goal || m.committedNextMonth > 0 || m.salesDoneThisMonth > 0,
        ),
      );
    } catch (err) {
      console.error('Error fetching metas data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async (): Promise<RutaGoal[]> => {
    const { data, error } = await supabase
      .from('ruta_goals')
      .select('*')
      .eq('unit_id', user!.unit_id)
      .eq('month', targetMonth)
      .eq('year', targetYear);

    if (error) {
      console.error('Error fetching goals:', error);
      return [];
    }
    return data || [];
  };

  const fetchCommittedReceivables = async (): Promise<Record<number, number>> => {
    const startISO = `${targetYear}-${String(targetMonth).padStart(2, '0')}-01`;
    const endDay = new Date(targetYear, targetMonth, 0).getDate();
    const endISO = `${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;

    const { data, error } = await supabase
      .from('installments')
      .select(`amount, contract:contracts!inner(ruta, unit_id, approval_status)`)
      .eq('contract.unit_id', user!.unit_id)
      .eq('contract.approval_status', 'approved')
      .gte('due_date', startISO)
      .lte('due_date', endISO);

    if (error) {
      console.error('Error fetching committed receivables:', error);
      return {};
    }

    const byRuta: Record<number, number> = {};
    (data || []).forEach((row: any) => {
      const ruta = row.contract?.ruta;
      if (ruta) {
        byRuta[ruta] = (byRuta[ruta] || 0) + Number(row.amount || 0);
      }
    });
    return byRuta;
  };

  const fetchReceivedNextMonth = async (): Promise<Record<number, number>> => {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select(`amount, late_fee, contract:contracts!inner(ruta)`)
      .eq('unit_id', user!.unit_id)
      .eq('status', 'approved')
      .gte('verified_at', targetStart.toISOString())
      .lte('verified_at', targetEnd.toISOString());

    if (error) {
      console.error('Error fetching received transactions:', error);
      return {};
    }

    const byRuta: Record<number, number> = {};
    (data || []).forEach((t: any) => {
      const ruta = t.contract?.ruta;
      if (ruta) {
        byRuta[ruta] = (byRuta[ruta] || 0) + Number(t.amount || 0) + Number(t.late_fee || 0);
      }
    });
    return byRuta;
  };

  const fetchSalesThisMonth = async (): Promise<{
    amounts: Record<number, number>;
    counts: Record<number, number>;
  }> => {
    const { data, error } = await supabase
      .from('contracts')
      .select('ruta, loan_amount')
      .eq('unit_id', user!.unit_id)
      .eq('approval_status', 'approved')
      .gte('created_at', salesStart.toISOString())
      .lte('created_at', salesEnd.toISOString());

    if (error) {
      console.error('Error fetching sales:', error);
      return { amounts: {}, counts: {} };
    }

    const amounts: Record<number, number> = {};
    const counts: Record<number, number> = {};
    (data || []).forEach((c: any) => {
      if (c.ruta) {
        amounts[c.ruta] = (amounts[c.ruta] || 0) + Number(c.loan_amount || 0);
        counts[c.ruta] = (counts[c.ruta] || 0) + 1;
      }
    });
    return { amounts, counts };
  };

  const fetchAvgTicket = async (): Promise<number> => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const { data, error } = await supabase
      .from('contracts')
      .select('loan_amount')
      .eq('unit_id', user!.unit_id)
      .eq('approval_status', 'approved')
      .gte('created_at', ninetyDaysAgo.toISOString());

    if (error || !data || data.length === 0) return 0;

    const total = data.reduce((sum: number, c: any) => sum + Number(c.loan_amount || 0), 0);
    return total / data.length;
  };

  const getSalesWorkdays = (): { passed: number; total: number; remaining: number } => {
    let total = 0;
    let passed = 0;
    const d = new Date(salesYear, salesMonth - 1, 1);
    while (d.getMonth() === salesMonth - 1) {
      if (isWorkday(d)) {
        total++;
        if (isCurrentSalesMonth && d <= today) passed++;
        else if (!isCurrentSalesMonth) passed = total;
      }
      d.setDate(d.getDate() + 1);
    }
    return { passed, total, remaining: total - passed };
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatCompact = (value: number) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1).replace('.0', '')}k`;
    return value.toFixed(0);
  };

  const getProgressColor = (pct: number, daysPct: number) => {
    const expectedPct = daysPct * 100;
    if (pct >= expectedPct * 0.9) return 'bg-emerald-500';
    if (pct >= expectedPct * 0.6) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getStatusLabel = (pct: number, daysPct: number) => {
    const expectedPct = daysPct * 100;
    if (pct >= expectedPct * 0.9)
      return { text: 'No ritmo', color: 'text-emerald-600 bg-emerald-50' };
    if (pct >= expectedPct * 0.6)
      return { text: 'Atenção', color: 'text-amber-600 bg-amber-50' };
    return { text: 'Atrasado', color: 'text-red-600 bg-red-50' };
  };

  const handlePrevMonth = () => {
    const d = new Date(salesYear, salesMonth - 2, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    const d = new Date(salesYear, salesMonth, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const openConfig = () => {
    const rutas = getVisibleRutas(user);
    setConfigGoals(
      rutas.map(ruta => {
        const existing = metrics.find(m => m.ruta === ruta)?.goal;
        return { ruta, target: existing?.collection_target || DEFAULT_COLLECTION_TARGET };
      }),
    );
    setShowConfigModal(true);
  };

  const saveConfig = async () => {
    if (!user?.unit_id) return;
    setSavingConfig(true);
    try {
      for (const { ruta, target } of configGoals) {
        if (target <= 0) continue;
        const existing = metrics.find(m => m.ruta === ruta)?.goal;
        const reinvestPct = existing?.reinvestment_pct || DEFAULT_REINVESTMENT_PCT;
        const salesTarget = (target * reinvestPct) / 100;
        const { error } = await supabase.from('ruta_goals').upsert(
          {
            ruta,
            unit_id: user.unit_id,
            month: targetMonth,
            year: targetYear,
            collection_target: target,
            sales_target: salesTarget,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'ruta,unit_id,month,year' },
        );
        if (error) console.error('Error saving goal:', error);
      }
      setShowConfigModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving config:', err);
    } finally {
      setSavingConfig(false);
    }
  };

  const salesWorkdays = getSalesWorkdays();
  const salesDaysPct = salesWorkdays.total > 0 ? salesWorkdays.passed / salesWorkdays.total : 0;

  const totalCollectionTarget = metrics.reduce(
    (s, m) => s + (m.goal?.collection_target || DEFAULT_COLLECTION_TARGET),
    0,
  );
  const totalCommitted = metrics.reduce((s, m) => s + m.committedNextMonth, 0);
  const totalReceivablesGap = Math.max(0, totalCollectionTarget - totalCommitted);
  const totalSalesTarget = metrics.reduce((s, m) => {
    const target = m.goal?.collection_target || DEFAULT_COLLECTION_TARGET;
    const pct = m.goal?.reinvestment_pct || DEFAULT_REINVESTMENT_PCT;
    return s + (target * pct) / 100;
  }, 0);
  const totalSalesDone = metrics.reduce((s, m) => s + m.salesDoneThisMonth, 0);
  const totalSalesMissing = Math.max(0, totalSalesTarget - totalSalesDone);
  const totalSalesCount = metrics.reduce((s, m) => s + m.salesCount, 0);
  const totalCommittedPct =
    totalCollectionTarget > 0 ? (totalCommitted / totalCollectionTarget) * 100 : 0;
  const totalSalesPct = totalSalesTarget > 0 ? (totalSalesDone / totalSalesTarget) * 100 : 0;

  const salesPerRemainingDay =
    salesWorkdays.remaining > 0 ? totalSalesMissing / salesWorkdays.remaining : 0;
  const salesCountPerRemainingDay =
    avgTicket > 0 && salesWorkdays.remaining > 0
      ? Math.ceil(totalSalesMissing / avgTicket / salesWorkdays.remaining)
      : 0;

  if (user?.role !== 'manager') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Acesso restrito a gestores.</p>
      </div>
    );
  }

  const salesMonthLabel = new Date(salesYear, salesMonth - 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  const targetMonthLabel = new Date(targetYear, targetMonth - 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-7 h-7 text-teal-600" />
            Metas por Ruta
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            Vendendo em <span className="font-semibold capitalize">{salesMonthLabel}</span> para
            bater a meta de recebimento em{' '}
            <span className="font-semibold capitalize">{targetMonthLabel}</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1.5">
            <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium capitalize min-w-[130px] text-center">
              {salesMonthLabel}
            </span>
            <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={openConfig}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            Configurar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Target className="w-4 h-4" />
                Meta de recebimento
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(totalCollectionTarget)}
              </p>
              <p className="text-xs text-gray-400 mt-1 capitalize">para {targetMonthLabel}</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                Já a receber
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalCommitted)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {totalCommittedPct.toFixed(0)}% da meta | Gap:{' '}
                {formatCurrency(totalReceivablesGap)}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <ShoppingCart className="w-4 h-4" />
                Vendas este mês
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(totalSalesDone)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                de {formatCurrency(totalSalesTarget)} ({totalSalesPct.toFixed(0)}%) |{' '}
                {totalSalesCount} vendas
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <TrendingUp className="w-4 h-4" />
                Faltam vender
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(totalSalesMissing)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {avgTicket > 0 && (
                  <>~{Math.ceil(totalSalesMissing / avgTicket)} vendas | </>
                )}
                {salesWorkdays.remaining > 0
                  ? `${formatCurrency(salesPerRemainingDay)}/dia útil${
                      salesCountPerRemainingDay > 0 ? ` (~${salesCountPerRemainingDay}/dia)` : ''
                    }`
                  : 'Mês encerrado'}
              </p>
            </div>
          </div>

          {/* Info banner */}
          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
            <div className="text-sm text-teal-900">
              <p className="font-medium">Como funciona</p>
              <p className="text-teal-800 mt-1">
                O ciclo de recebimento é sempre um mês à frente. As vendas fechadas em{' '}
                <span className="font-semibold capitalize">{salesMonthLabel}</span> viram parcelas
                que vencem em{' '}
                <span className="font-semibold capitalize">{targetMonthLabel}</span>. O sistema
                subtrai o que já está agendado a receber para calcular quanto ainda falta vender
                neste mês.
              </p>
              <p className="text-teal-800 mt-1">
                {salesWorkdays.passed} dia{salesWorkdays.passed !== 1 ? 's' : ''} útil
                {salesWorkdays.passed !== 1 ? 'eis' : ''} passado
                {salesWorkdays.passed !== 1 ? 's' : ''} | {salesWorkdays.remaining} restante
                {salesWorkdays.remaining !== 1 ? 's' : ''} no mês de vendas.
                {avgTicket > 0 && ` Ticket médio: ${formatCurrency(avgTicket)}.`}
              </p>
            </div>
          </div>

          {/* Ruta Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {metrics.map(m => {
              const target = m.goal?.collection_target || DEFAULT_COLLECTION_TARGET;
              const reinvestPct = m.goal?.reinvestment_pct || DEFAULT_REINVESTMENT_PCT;
              const salesTarget = (target * reinvestPct) / 100;

              const committedPct = target > 0 ? (m.committedNextMonth / target) * 100 : 0;
              const receivablesGap = Math.max(0, target - m.committedNextMonth);

              const salesPct = salesTarget > 0 ? (m.salesDoneThisMonth / salesTarget) * 100 : 0;
              const salesMissing = Math.max(0, salesTarget - m.salesDoneThisMonth);
              const salesMissingCount =
                avgTicket > 0 ? Math.ceil(salesMissing / avgTicket) : 0;
              const perDay =
                salesWorkdays.remaining > 0 ? salesMissing / salesWorkdays.remaining : 0;

              const status = getStatusLabel(salesPct, salesDaysPct);

              const profitTarget = target * ((m.goal?.profit_pct || 28.57) / 100);
              const expensesTarget = target * ((m.goal?.expenses_pct || 7.14) / 100);
              const lossTarget = target * ((m.goal?.loss_pct || 7.14) / 100);

              return (
                <div
                  key={m.ruta}
                  className="bg-white rounded-xl border border-gray-200 p-5 space-y-4"
                >
                  {/* Card Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                        <span className="text-lg font-bold text-teal-700">{m.ruta}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Ruta {m.ruta}</h3>
                        <p className="text-xs text-gray-400 capitalize">
                          Meta {targetMonthLabel}: {formatCurrency(target)}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${status.color}`}>
                      {status.text}
                    </span>
                  </div>

                  {/* Sales Progress (this month) */}
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-600 flex items-center gap-1">
                        <ShoppingCart className="w-3.5 h-3.5" />
                        Vendas em <span className="capitalize">{salesMonthLabel}</span>
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(m.salesDoneThisMonth)}{' '}
                        <span className="text-gray-400">({m.salesCount} vendas)</span>
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                          salesPct,
                          salesDaysPct,
                        )}`}
                        style={{ width: `${Math.min(100, salesPct)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Meta de vendas: {formatCurrency(salesTarget)} ({salesPct.toFixed(0)}%)
                    </p>
                    <p className="text-xs font-medium text-gray-700 mt-1">
                      Faltam vender: {formatCurrency(salesMissing)}
                      {salesMissingCount > 0 && ` (~${salesMissingCount} vendas)`}
                      {salesWorkdays.remaining > 0 && salesMissing > 0 && (
                        <>
                          {' '}
                          | {formatCurrency(perDay)}/dia útil
                        </>
                      )}
                    </p>
                  </div>

                  {/* Committed Receivables Progress (next month) */}
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Já a receber em <span className="capitalize">{targetMonthLabel}</span>
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(m.committedNextMonth)}{' '}
                        <span className="text-gray-400">({committedPct.toFixed(0)}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, committedPct)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Gap: {formatCurrency(receivablesGap)} |{' '}
                      {m.receivedNextMonth > 0
                        ? `Já recebido: ${formatCurrency(m.receivedNextMonth)}`
                        : 'Nenhum pagamento verificado ainda'}
                    </p>
                  </div>

                  {/* Distribution Buckets */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center p-2 bg-emerald-50 rounded-lg">
                      <p className="text-[10px] text-emerald-600 font-medium uppercase">Lucro</p>
                      <p className="text-sm font-bold text-emerald-700">
                        {formatCompact(profitTarget)}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <p className="text-[10px] text-gray-500 font-medium uppercase">Gastos</p>
                      <p className="text-sm font-bold text-gray-700">
                        {formatCompact(expensesTarget)}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-[10px] text-red-500 font-medium uppercase">Perda</p>
                      <p className="text-sm font-bold text-red-700">
                        {formatCompact(lossTarget)}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <p className="text-[10px] text-blue-500 font-medium uppercase">Reinvest.</p>
                      <p className="text-sm font-bold text-blue-700">
                        {formatCompact(salesTarget)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {metrics.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-2 capitalize">
                Nenhuma meta configurada para {targetMonthLabel}.
              </p>
              <button
                onClick={openConfig}
                className="text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                Configurar metas agora
              </button>
            </div>
          )}
        </>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold capitalize">
                  Metas de recebimento — {targetMonthLabel}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5 capitalize">
                  A meta de vendas de {salesMonthLabel} é derivada automaticamente.
                </p>
              </div>
              <button
                onClick={() => setShowConfigModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {configGoals.map((goal, idx) => {
                const existing = metrics.find(m => m.ruta === goal.ruta)?.goal;
                const pct = existing?.reinvestment_pct || DEFAULT_REINVESTMENT_PCT;
                const derivedSales = (goal.target * pct) / 100;
                return (
                  <div key={goal.ruta} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 mt-4">
                      <span className="text-sm font-bold text-teal-700">{goal.ruta}</span>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-500">
                        Ruta {goal.ruta} — Meta de recebimento
                      </label>
                      <input
                        type="number"
                        value={goal.target}
                        onChange={e => {
                          const updated = [...configGoals];
                          updated[idx].target = Number(e.target.value);
                          setConfigGoals(updated);
                        }}
                        className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        step={1000}
                        min={0}
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        Meta de vendas derivada ({pct.toFixed(2)}%):{' '}
                        <span className="font-medium text-gray-600">
                          {formatCurrency(derivedSales)}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveConfig}
                disabled={savingConfig}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {savingConfig ? 'Salvando...' : 'Salvar Metas'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
